import { useMemo } from 'react'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'

import { masks } from 'src/utils/masks'
import { fPercent } from 'src/utils/format-number'
import Iconify from 'src/components/iconify'
import Chart, { useChart } from 'src/components/chart'

import { SellerRow } from './seller-table'

const COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#f59e0b',
  '#10b981',
  '#06b6d4',
  '#3b82f6',
  '#ef4444',
  '#84cc16',
  '#a855f7',
]

type Props = {
  data: SellerRow[]
}

export default function SellerShareChart({ data }: Props) {
  const total = useMemo(() => data.reduce((acc, s) => acc + (s.totalSales || 0), 0), [data])

  const { labels, series, others } = useMemo(() => {
    const sorted = [...data].sort((a, b) => b.totalSales - a.totalSales)
    const top = sorted.slice(0, 8)
    const rest = sorted.slice(8)
    const restTotal = rest.reduce((acc, s) => acc + s.totalSales, 0)

    const _labels = top.map((s) => s.sellerName)
    const _series = top.map((s) => s.totalSales)
    if (restTotal > 0) {
      _labels.push(`Outros (${rest.length})`)
      _series.push(restTotal)
    }
    return { labels: _labels, series: _series, others: rest.length }
  }, [data])

  const options = useChart({
    chart: { type: 'donut' },
    labels,
    colors: COLORS,
    stroke: { width: 2, colors: ['#fff'] },
    dataLabels: { enabled: false },
    legend: {
      position: 'bottom',
      fontSize: '12px',
      fontWeight: 600,
      itemMargin: { horizontal: 8, vertical: 4 },
      markers: { radius: 6 },
    },
    tooltip: {
      y: {
        formatter: (val: any) =>
          `${masks.currencyAllPlatforms(val)} (${fPercent((val / total) * 100)})`,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '13px',
              fontWeight: 600,
              color: '#64748b',
              offsetY: -8,
            },
            value: {
              show: true,
              fontSize: '20px',
              fontWeight: 700,
              color: '#0f172a',
              offsetY: 4,
              formatter: (val: any) => masks.currencyAllPlatforms(Number(val)),
            },
            total: {
              show: true,
              label: 'Total',
              color: '#64748b',
              fontSize: '12px',
              fontWeight: 600,
              formatter: () => masks.currencyAllPlatforms(total),
            },
          },
        },
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: { legend: { position: 'bottom' } },
      },
    ],
  })

  if (!data.length) return null

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardHeader
        title={
          <Stack direction='row' alignItems='center' spacing={1.25}>
            <Iconify icon='solar:pie-chart-3-bold' width={20} sx={{ color: 'primary.main' }} />
            <Typography variant='subtitle1' fontWeight={700} sx={{ fontSize: 15 }}>
              Distribuição do Faturamento
            </Typography>
            <Chip
              size='small'
              label='Top 8'
              color='primary'
              variant='soft'
              sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
            />
          </Stack>
        }
        subheader={
          others > 0 ? (
            <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11.5 }}>
              + {others} outros vendedores agrupados
            </Typography>
          ) : (
            <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11.5 }}>
              Participação de cada vendedor no total
            </Typography>
          )
        }
        sx={{ py: 1.5, px: 2 }}
      />
      <Box sx={{ px: 2, pb: 2 }}>
        <Chart type='donut' series={series} options={options} height={340} />
      </Box>
    </Card>
  )
}
